import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TemplateDTO } from "@retrospecter/shared/types";
import { useAuth } from "../hooks/useAuth";
import { apiGet, apiPost } from "../api/client";
import { TemplatePreview } from "../components/TemplatePreview";
import { useTranslation } from "../i18n";

/**
 * Choosing a format for the session.
 *
 * This used to be a dropdown on the home page, which asked a facilitator to
 * pick between eight names with no idea what board each one produces. A screen
 * of its own has room to show the shape of every board next to its name.
 */
export function NewRetroPage() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { t, plural, templateName, templateDescription } = useTranslation();

  const [templates, setTemplates] = useState<TemplateDTO[]>([]);
  const [templatesFailed, setTemplatesFailed] = useState(false);
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!auth.loading && !auth.staff) {
      navigate("/login");
      return;
    }

    if (!auth.staff) return;

    const load = async () => {
      try {
        const data: TemplateDTO[] = await apiGet("/api/templates");
        setTemplates(data);
        // Nothing is pre-selected: the point of this screen is that the choice
        // is made deliberately, having looked at the boards.
      } catch (err) {
        console.error("Failed to fetch templates:", err);
        setTemplatesFailed(true);
      }
    };

    load();
  }, [auth, navigate]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !templateId) return;

    setError("");
    setCreating(true);

    try {
      const data = await apiPost("/api/retrospectives", {
        name: name.trim(),
        templateId,
      });
      navigate(`/r/${data.publicId}`);
    } catch (err) {
      console.error("Create failed:", err);
      setError(t("create.error"));
      setCreating(false);
    }
  };

  if (auth.loading) {
    return (
      <div className="page-container flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-2 border-nm-primary border-t-transparent" />
          <p className="font-medium text-ink-muted">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!auth.staff) return null;

  return (
    <form onSubmit={create} className="page-container pb-28">
      <div className="mb-8 animate-fadeIn">
        <Link
          to="/"
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted transition-colors hover:text-nm-accent"
        >
          <span aria-hidden="true">←</span>
          {t("create.back")}
        </Link>

        <h1 className="mb-1 text-3xl font-bold tracking-tight text-ink md:text-4xl">
          {t("create.title")}
        </h1>
        <p className="text-ink-muted">{t("create.subtitle")}</p>
      </div>

      <div className="card animate-slideUp mb-8 p-6">
        <label
          htmlFor="name"
          className="mb-1.5 block text-sm font-semibold text-ink-soft"
        >
          {t("home.nameLabel")}
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          disabled={creating}
          autoFocus
          placeholder={t("home.namePlaceholder")}
          className="input-field max-w-xl disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>

      <fieldset className="animate-slideUp">
        <legend className="mb-1 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
          {t("create.chooseTemplate")}
        </legend>
        <p className="mb-4 text-sm text-ink-faint">{t("create.chooseHint")}</p>

        {templatesFailed && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500">
            {t("create.loadError")}
          </p>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => {
            const selected = template.id === templateId;

            return (
              <label
                key={template.id}
                data-testid="template-card"
                data-template={template.name}
                // The radio itself is visually hidden, so the card has to show
                // the focus ring for anyone arrowing through the grid.
                className={`card group flex cursor-pointer flex-col gap-3 p-4 transition-all duration-200 focus-within:ring-2 focus-within:ring-nm-primary focus-within:ring-offset-2 focus-within:ring-offset-canvas ${
                  selected
                    ? "border-transparent shadow-raised ring-2 ring-nm-accent"
                    : "hover:-translate-y-0.5 hover:shadow-raised"
                }`}
              >
                {/* A real radio, visually hidden: it brings keyboard arrow
                    navigation and the selected state for free. */}
                <input
                  type="radio"
                  name="template"
                  value={template.id}
                  checked={selected}
                  onChange={() => setTemplateId(template.id)}
                  disabled={creating}
                  className="sr-only"
                />

                <TemplatePreview template={template} />

                <div className="min-w-0">
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <h2 className="truncate text-base font-bold text-ink">
                      {templateName(template.name)}
                    </h2>
                    <span className="whitespace-nowrap text-xs text-ink-faint">
                      {plural("create.columnCount", template.columns.length)}
                    </span>
                  </div>

                  <p className="text-sm leading-snug text-ink-soft">
                    {templateDescription(template.name, template.description)}
                  </p>
                </div>

                <span
                  aria-hidden="true"
                  className={`mt-auto text-xs font-semibold ${
                    selected ? "text-nm-accent" : "text-transparent"
                  }`}
                >
                  ✓ {t("create.selected")}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* The action follows you down the grid: the templates are the page, and
          the button must not be stranded below eight of them. */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-canvas/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-3 sm:px-6">
          <p className="text-xs text-ink-faint">{t("home.draftHint")}</p>

          <div className="flex items-center gap-3">
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={creating || !name.trim() || !templateId}
              data-testid="create-retro"
              className="btn-accent whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating ? t("home.creating") : t("home.create")}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
